'use client';

import { type CustomFieldProps, useField } from 'informed';
import { type InputHTMLAttributes, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { ValidatorMessage } from '@/helpers/validators';
import type { CustomFormBlockFieldProps } from './contracts';
import { InputBlock, type InputBlockProps } from './primitives/InputBlock';
import type { InputStructureProps } from './structures/InputStructure';
import { getErrorTranslation } from './utils';

export function asBlockField<Attr, P extends InputStructureProps & Attr, V>(
  Component: React.ComponentType<P>
): React.ComponentType<CustomFieldProps<CustomFormBlockFieldProps<V>, V> & P> {
  return ({
    transformValue,
    touchOnChange,
    onChange,
    onBlur,
    onFocus,
    translateError = true,
    error: propsError,
    preventValidation,
    validatorWarning,
    ...fieldProps
  }) => {
    const { t } = useTranslation();

    const { fieldState, fieldApi, render, ref, userProps } = useField<P, V>(
      fieldProps
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (validatorWarning) {
          const value = fieldApi.getValue();
          const warningMessage = validatorWarning(value as string);
          const existingWarning = fieldApi.getError();
          if (warningMessage === existingWarning) {
            fieldApi.setError('' as V); // Clear warning on change
          }
        }

        fieldApi.setValue(
          transformValue ? transformValue(value) : (value as V),
          e
        );
        touchOnChange && fieldApi.setTouched(true);

        if (onChange) {
          onChange(e);
        }
      },
      [onChange, fieldApi, transformValue, touchOnChange, validatorWarning]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (preventValidation) {
          e.preventDefault();
          return;
        }

        fieldApi.setTouched(true);

        if (onBlur) {
          onBlur(e);
        }

        if (validatorWarning) {
          const value = (e.target as HTMLInputElement).value;
          const warningMessage = validatorWarning(value);
          if (warningMessage) {
            fieldApi.setError(warningMessage as V); // Show warning without blocking submit
          }
        }
      },
      [onBlur, fieldApi, preventValidation]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        if (onFocus) {
          onFocus(e);
        }
      },
      [onFocus]
    );

    const getError = useCallback(() => {
      if (propsError) return propsError;

      if (fieldState.error != null && translateError) {
        return getErrorTranslation(t, fieldState.error as ValidatorMessage);
      }
      return fieldState.error;
    }, [t, propsError, translateError, fieldState.error]);

    return render(
      <Component
        {...userProps}
        {...fieldState}
        {...fieldApi}
        error={getError()}
        id={useId()}
        name={fieldProps.name}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        ref={ref}
        value={fieldState.value == null ? '' : fieldState.value}
      />
    );
  };
}

export const Input = asBlockField<
  InputHTMLAttributes<HTMLInputElement>,
  InputBlockProps & React.RefAttributes<HTMLInputElement>,
  string
>(InputBlock);
